import { defineStore } from 'pinia';
import { supabase, signUp, signIn, signOut, getUser, fetchProfile, updateProfile } from '../services/supabase';

export const useUserStore = defineStore('user', {
  state: () => ({
    user: null,
    profile: null,
    loading: false,
    error: null
  }),
  
  getters: {
    isAuthenticated: (state) => !!state.user,
    hasVoiceModel: (state) => state.user?.voice_model_id ? true : false
  },
  
  actions: {
    async register(email, password) {
      this.loading = true;
      this.error = null;
      
      try {
        // Log the registration attempt for debugging
        console.log('Attempting to register with email:', email);
        
        const data = await signUp(email, password);
        console.log('Supabase signUp response:', data);
        
        // Handle different response structures
        if (data.user) {
          console.log('User data found in data.user');
          this.user = data.user;
        } else if (data.session && data.session.user) {
          console.log('User data found in data.session.user');
          this.user = data.session.user;
        } else if (data) {
          console.log('No user data found in expected locations, using available data');
          // If user is not directly available but we have some data
          this.user = { email, id: data.id || 'pending' };
        } else {
          throw new Error('Could not retrieve user data after registration');
        }
        
        // Try to fetch user profile
        if (this.user && this.user.id && this.user.id !== 'pending') {
          try {
            this.profile = await fetchProfile(this.user.id);
            console.log('User profile fetched:', this.profile);
          } catch (profileError) {
            console.warn('Could not fetch user profile:', profileError);
            // This is not a critical error, so we don't throw it
          }
        }
        
        return data;
      } catch (error) {
        console.error('Registration error:', error);
        this.error = error.message;
        throw error;
      } finally {
        this.loading = false;
      }
    },
    
    async login(email, password) {
      this.loading = true;
      this.error = null;
      
      try {
        console.log('Attempting to login with email:', email);
        
        const data = await signIn(email, password);
        console.log('Supabase login response:', data);
        
        // Handle different response structures
        if (data.user) {
          this.user = data.user;
          
          // Fetch user profile
          try {
            this.profile = await fetchProfile(data.user.id);
          } catch (profileError) {
            console.warn('Could not fetch user profile:', profileError);
          }
        } else if (data.session && data.session.user) {
          this.user = data.session.user;
          
          // Fetch user profile
          try {
            this.profile = await fetchProfile(data.session.user.id);
          } catch (profileError) {
            console.warn('Could not fetch user profile:', profileError);
          }
        } else {
          throw new Error('Could not retrieve user data after login');
        }
        
        return data;
      } catch (error) {
        console.error('Login error:', error);
        this.error = error.message;
        throw error;
      } finally {
        this.loading = false;
      }
    },
    
    async logout() {
      this.loading = true;
      this.error = null;
      
      try {
        const success = await signOut();
        
        // Always clear the user and profile data, even if signOut fails
        this.user = null;
        this.profile = null;
        
        if (!success) {
          console.warn('Logout may not have completed successfully on the server, but local state has been cleared');
        }
        
        return success;
      } catch (error) {
        console.error('Logout error:', error);
        this.error = error.message;
        
        // Still clear the local state even if there was an error
        this.user = null;
        this.profile = null;
        
        return false;
      } finally {
        this.loading = false;
      }
    },
    
    async fetchUser() {
      this.loading = true;
      this.error = null;
      
      try {
        const user = await getUser();
        console.log('Supabase getUser response:', user);
        
        if (user) {
          this.user = user;
          
          // Fetch user profile
          try {
            this.profile = await fetchProfile(user.id);
          } catch (profileError) {
            console.warn('Could not fetch user profile:', profileError);
          }
          
          return user;
        } else {
          this.user = null;
          this.profile = null;
          return null;
        }
      } catch (error) {
        console.error('Fetch user error:', error);
        this.error = error.message;
        this.user = null;
        this.profile = null;
        return null;
      } finally {
        this.loading = false;
      }
    },
    
    async updateUserProfile(profile) {
      this.loading = true;
      this.error = null;
      
      try {
        if (!this.user) {
          throw new Error('User not authenticated');
        }
        
        const updatedProfile = await updateProfile(this.user.id, profile);
        
        // Update local profile data
        this.profile = {
          ...this.profile,
          ...updatedProfile
        };
        
        return updatedProfile;
      } catch (error) {
        console.error('Update profile error:', error);
        this.error = error.message;
        throw error;
      } finally {
        this.loading = false;
      }
    }
  }
});
